// Preview metadata shape + the compact-vs-hero layout heuristic. The metadata
// itself is fetched and parsed server-side by the bridge's unfurl endpoint
// (see generate.ts / packages/ws-tcp-proxy/unfurl.mjs); this module only holds
// the types the frontend consumes and the layout rule.

export type PreviewLayout = 'compact' | 'hero'

export type LinkMetadata = {
  url: string
  host: string
  title: string
  description: string | null
  /** Absolute URL of the preview image, or null if the page declares none. */
  imageUrl: string | null
  imageWidth: number | null
  imageHeight: number | null
  isVideo: boolean
  /** Layout the metadata asks for; the user can still override it. */
  suggestedLayout: PreviewLayout
}

/**
 * Decide compact vs hero from the metadata alone (no hardcoded site list).
 * The site's own declarations win: a large-image twitter card or a video wants
 * a hero; a plain summary card or a small/square image wants compact.
 */
export function chooseLayout(opts: {
  isVideo: boolean
  twitterCard: string | null
  imageUrl: string | null
  imageWidth: number | null
  imageHeight: number | null
}): PreviewLayout {
  if (opts.isVideo) {
    return 'hero'
  }
  const card = opts.twitterCard?.toLowerCase()
  if (card === 'summary_large_image' || card === 'player') {
    return 'hero'
  }
  if (card === 'summary') {
    return 'compact'
  }
  if (opts.imageUrl && opts.imageWidth && opts.imageHeight) {
    const wide = opts.imageWidth / opts.imageHeight >= 1.5
    const bigEnough = opts.imageWidth >= 480
    if (wide && bigEnough) {
      return 'hero'
    }
    // Small or square images (favicons, avatars) read better as a side thumb.
    return 'compact'
  }
  // Image with unknown dimensions: default to compact; the renderer measures the
  // decoded image and can be nudged to hero there if it turns out wide.
  return 'compact'
}
